import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Phone } from "lucide-react";
import { CustomerNotes, type NoteView } from "@/design/CustomerNotes";
import { CustomersList } from "@/design/CustomersList";
import { MealPlanList } from "@/design/MealPlanReader";
import type { CustomerMealPlansView } from "@/design/mealPlanViews";
import { buttonClass } from "@/design/buttons";
import { DataUnavailable } from "@/design/DataUnavailable";
import { PageShell } from "@/design/ui";
import { formStateFor as notesStateFor } from "@/modules/customer_records/commands/notesCopy";
import { formatLebanesePhone } from "@/modules/identity/surfaces";
import type { BusinessCustomer } from "@/modules/insights/surfaces";
import { AppLink } from "~/components/AppLink";
import { BusinessPage } from "~/components/BusinessChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { NotFoundScreen } from "~/screens/NotFoundScreen";
import { apiFormAction } from "~/screens/business/SettingsToolScreens";
import { useQuery } from "~/lib/useQuery";

/**
 * The clinic's customer directory and one person's file (R22 T14).
 *
 * Both are open to **both tiers** (H26), which is deliberate and is the one
 * documented exception to the view-only Practitioner tier: the person who saw
 * the patient is the person with something to write (H42).
 */

// ── The directory (docs/ux/screens/CustomersScreen.md) ───────────────────────

interface CustomersPayload {
  customers: BusinessCustomer[];
  scope: "mine" | "all";
  hasOwn: boolean;
  /** H53: only a Nutritionist clinic is shown the way to its templates. */
  offersMealPlans: boolean;
}

/**
 * **The URL is what says which search is on screen**, exactly as it is on the
 * public search: the scope toggle and the search box both navigate, and this
 * re-reads. That keeps a filtered directory shareable between colleagues and
 * survivable across a reload, which matters more here than it looks, because
 * "the customer I was just looking at" is a thing people say to each other.
 *
 * The **default scope comes from the server**, since it is the same H26
 * question the read itself answers. A client guessing it from the tier would
 * show a practitioner everybody's customers for one frame.
 */
export function CustomersScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const search = params.toString();
  const query = params.get("q") ?? "";

  const customers = useQuery<CustomersPayload>(
    `/business/customers${search ? `?${search}` : ""}`,
  );

  return (
    <BusinessPage>
      <PageShell>
        <div className="mx-auto w-full max-w-2xl">
          {customers.data?.offersMealPlans && (
            <div className="mb-4 flex justify-end">
              <Link
                to="/business/meal-plan-templates"
                className="text-sm font-semibold text-accent hover:underline"
              >
                Meal plan templates
              </Link>
            </div>
          )}
          {customers.loading && !customers.data ? (
            <ScreenPending />
          ) : (
            <CustomersList
              customers={customers.data?.customers ?? null}
              query={query}
              scope={customers.data?.scope ?? "all"}
              hasOwn={customers.data?.hasOwn ?? false}
              failed={customers.failure !== null}
              configured={customers.failure !== "not_configured"}
              Link={AppLink}
              onSearch={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                const next = new URLSearchParams();
                const typed = String(form.get("q") ?? "").trim();
                if (typed) next.set("q", typed);
                const scope = String(form.get("scope") ?? "");
                if (scope) next.set("scope", scope);
                const qs = next.toString();
                void navigate(
                  `/business/customers${qs ? `?${qs}` : ""}`,
                );
              }}
            />
          )}
        </div>
      </PageShell>
    </BusinessPage>
  );
}

// ── One person's file (docs/ux/screens/CustomerFileScreen.md, H42) ───────────

interface FilePayload {
  customer: BusinessCustomer;
  notes: NoteView[];
  visits: { id: string; label: string }[];
  maxLength: number;
  mealPlans: CustomerMealPlansView;
}

/**
 * **A Consumer never reaches this screen and never sees its contents
 * anywhere.** That is not enforced here: no consumer-facing query reads the
 * `customer_records` schema at all, which is a stronger guarantee than any
 * check this component could make.
 *
 * A person who has never booked at this clinic is a 404, and so is an id that
 * never existed. The server answers both the same way on purpose, so this
 * screen cannot be used to ask whether somebody is a Bookr customer.
 */
export function CustomerFileScreen() {
  const { consumerId = "" } = useParams();
  const file = useQuery<FilePayload>(
    consumerId ? `/business/customers/${consumerId}/notes` : null,
  );
  const reload = () => file.reload();

  if (file.failure === "not_found") return <NotFoundScreen />;

  return (
    <BusinessPage>
      <PageShell>
        <div className="mx-auto w-full max-w-2xl">
          <Link
            to="/business/customers"
            className="mb-4 inline-block text-sm font-medium text-accent"
          >
            ← Back to customers
          </Link>

          {file.loading && !file.data ? (
            <ScreenPending />
          ) : file.failure || !file.data ? (
            <DataUnavailable
              configured={file.failure !== "not_configured"}
              what="this customer's file"
            />
          ) : (
            <>
              <IdentityCard customer={file.data.customer} />
              <MealPlansSection
                consumerId={consumerId}
                customerName={file.data.customer.name}
                view={file.data.mealPlans}
              />
              <CustomerNotes
                consumerId={consumerId}
                notes={file.data.notes}
                visits={file.data.visits}
                maxLength={file.data.maxLength}
                commands={{
                  add: apiFormAction({
                    method: "POST",
                    path: () => `/business/customers/${consumerId}/notes`,
                    render: notesStateFor,
                    onSuccess: reload,
                  }),
                  edit: apiFormAction({
                    method: "PUT",
                    path: (form) =>
                      `/business/notes/${String(form.get("noteId") ?? "")}`,
                    render: notesStateFor,
                    onSuccess: reload,
                  }),
                  remove: apiFormAction({
                    method: "DELETE",
                    path: (form) =>
                      `/business/notes/${String(form.get("noteId") ?? "")}`,
                    render: notesStateFor,
                    onSuccess: reload,
                  }),
                }}
              />
            </>
          )}
        </div>
      </PageShell>
    </BusinessPage>
  );
}

/**
 * The plans this clinic gave the person (H53), above the notes because unlike
 * a note a plan is something the person holds too.
 *
 * Not rendered at all for a clinic that has never been a nutrition clinic and
 * has no plans: a section that can only say "not for you" is noise. A clinic
 * that dropped the Category still sees the plans it gave, without the button.
 */
function MealPlansSection({
  consumerId,
  customerName,
  view,
}: {
  consumerId: string;
  customerName: string;
  view: CustomerMealPlansView;
}) {
  if (!view.available && view.plans.length === 0) return null;
  return (
    <section className="mb-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-bold text-ink">Meal plans</h2>
        {view.available && (
          <Link
            to={`/business/customers/${consumerId}/meal-plans/new`}
            className={buttonClass("secondary", "min-h-9 py-1.5 text-sm")}
          >
            New meal plan
          </Link>
        )}
      </div>
      <MealPlanList
        plans={view.plans}
        hrefFor={(planId) => `/business/meal-plans/${planId}`}
        Link={AppLink}
        empty={`No meal plans for ${customerName} yet. ${customerName} sees a plan only once you share it.`}
      />
    </section>
  );
}

/** Who this file is about, and the one control a clinic actually reaches for. */
function IdentityCard({ customer }: { customer: BusinessCustomer }) {
  return (
    <div className="mb-6 rounded-xl border border-line bg-surface p-5 shadow-sm">
      <h1 className="text-xl font-bold text-ink">{customer.name}</h1>
      <p className="mt-1 text-sm text-muted">
        {customer.bookingCount}{" "}
        {customer.bookingCount === 1 ? "booking" : "bookings"} with you
      </p>
      {customer.phone && (
        <a
          href={`tel:${customer.phone}`}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
        >
          <Phone className="size-3.5 shrink-0" aria-hidden strokeWidth={1.8} />
          {formatLebanesePhone(customer.phone)}
        </a>
      )}
    </div>
  );
}
